import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { vendorCategories as defaultCategories } from '@/lib/vendorCategories';
import { categoryNameToSeoPath } from '@/lib/vendorCategorySeo';
import { getSeoPreviewMode } from '@/lib/seoPreviewMode';
import { isCategoryEnabled, mergeOrderedCategoryObjects } from '@/lib/vendorCategorySource';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [categories, setCategories] = useState(defaultCategories);
  const location = useLocation();
  const seoPreviewMode = getSeoPreviewMode(location.search);

  useEffect(() => {
    // Fetch custom category order if available
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        const apiCategories = Array.isArray(response.data) ? response.data : [];
        const categoriesSource = seoPreviewMode
          ? apiCategories
          : apiCategories.filter(isCategoryEnabled);

        setCategories(mergeOrderedCategoryObjects(categoriesSource, defaultCategories));
      } catch (error) {
        // Use default categories on error
        console.log('Using default categories');
      }
    };
    fetchCategories();
  }, [seoPreviewMode]);

  return (
    <footer className="bg-[#5C0120] text-white">
      <div className="container-wedding section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/dbazxz6j_logo%20wht%2026%20cropped.png" 
              alt="Gibraltar Wedding Expo" 
              className="h-24 md:h-32 w-auto"
            />
            <p className="text-white/90 text-sm leading-relaxed">
              Discover the finest wedding vendors in Gibraltar. Your perfect Mediterranean wedding starts here.
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-4 pt-2">
              <a 
                href="https://www.facebook.com/gibraltarweddingexpo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-[#D4AF37] transition-colors"
                aria-label="Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/gibraltarweddingexpo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-[#D4AF37] transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="space-y-4 lg:col-span-2">
            <h4 className="text-lg font-semibold text-white uppercase tracking-wide">Categories</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              {categories.map((category) => (
                <li key={category.slug || category.name}>
                  <Link
                    to={categoryNameToSeoPath(category.name)}
                    className="text-white/80 hover:text-[#D4AF37] text-sm transition-colors duration-200"
                    data-testid={`footer-category-link-${String(category.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white uppercase tracking-wide">Contact</h4>
            <ul className="space-y-2 text-white/80 text-sm">
              <li>Gibraltar</li>
              <li>+350 56690000</li>
              <li>
                <a href="mailto:admin@gibraltarweddingexpo.online" className="hover:text-[#D4AF37] transition-colors">
                  admin@gibraltarweddingexpo.online
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/30 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/90 text-sm">
            © {currentYear} Gibraltar Wedding Expo. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <span>Made and Presented by</span>
            <span className="text-[#D4AF37] font-medium">MAZ Photography</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_72531038-14f0-42cd-8641-db553410a76f/artifacts/6znzvohh_MAZlogo.png" 
              alt="MAZ Photography" 
              className="h-8 w-auto"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
